import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ChatSidebar from "./ChatSidebar";
import ChatWindow from "./ChatWindow";
import ChatInput from "./ChatInput";
import FlashAssistantLayout from "./FlashAssistantLayout";
import styles from "./ChatWorkspace.module.scss";
import flashStyles from "./FlashAssistant.module.scss";
import AIPortalLogo from "./AIPortalLogo";
import {
  createConversation,
  deleteConversation,
  editMessage,
  getConversationMessages,
  getConversations,
  getModels,
  getMyUsage,
  reactToMessage,
  regenerateMessage,
  renameConversation,
  streamChat,
  continueMessage,
  uploadAttachment,
} from "./chatApi";

function buildOptimisticUserMessage(text, attachments) {
  return {
    id: `user-${Date.now()}`,
    role: "user",
    content: text,
    attachments: attachments || [],
    created_at: new Date().toISOString(),
  };
}

function buildOptimisticAssistantMessage() {
  return {
    id: `assistant-${Date.now()}`,
    role: "assistant",
    content: "",
    created_at: new Date().toISOString(),
    status: "streaming",
  };
}

const FLASH_SUGGESTION_CHIPS = [
  "Show enquiries",
  "Show conversions",
  "Top sources this month",
  "Students needing follow-up",
];

export default function FlashChatWorkspace({ onClose, fullScreen, inlineMode, enterpriseLayout }) {
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("openai/gpt-4o-mini");
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [usage, setUsage] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);

  const loadBootstrap = useCallback(async () => {
    try {
      const [modelData, conversationData, usageData] = await Promise.all([
        getModels(),
        getConversations(),
        getMyUsage(),
      ]);
      const nextModels = modelData.models || [];
      setModels(nextModels);
      if (nextModels.length) {
        setSelectedModel((prev) => prev || nextModels[0].id);
      }
      const items = conversationData.conversations || [];
      setConversations(items);
      setUsage(usageData);
      setActiveConversation((prev) => prev || items[0] || null);
    } catch (error) {
      toast.error(error.message || "Failed to load Abyss.");
    }
  }, []);

  useEffect(() => {
    loadBootstrap();
  }, [loadBootstrap]);

  const loadMessages = useCallback(async (conversation) => {
    if (!conversation?.id) {
      setMessages([]);
      return;
    }
    try {
      const data = await getConversationMessages(conversation.id, 1, 100);
      setMessages(data.messages || []);
      setSelectedModel(conversation.selected_model || selectedModel);
    } catch (error) {
      toast.error(error.message || "Failed to load messages.");
    }
  }, [selectedModel]);

  useEffect(() => {
    loadMessages(activeConversation);
  }, [activeConversation, loadMessages]);

  const refreshConversations = useCallback(async () => {
    const data = await getConversations();
    setConversations(data.conversations || []);
  }, []);

  const handleCreateConversation = useCallback(async () => {
    try {
      const data = await createConversation({ model: selectedModel });
      const conversation = data.conversation;
      setConversations((prev) => [conversation, ...prev]);
      setActiveConversation(conversation);
      setMessages([]);
    } catch (error) {
      toast.error(error.message || "Failed to create conversation.");
    }
  }, [selectedModel]);

  const handleRenameConversation = useCallback(async (conversation, title) => {
    try {
      const data = await renameConversation(conversation.id, title);
      setConversations((prev) =>
        prev.map((item) => (item.id === conversation.id ? data.conversation : item))
      );
      if (activeConversation?.id === conversation.id) {
        setActiveConversation(data.conversation);
      }
    } catch (error) {
      toast.error(error.message || "Failed to rename chat.");
    }
  }, [activeConversation]);

  const handleDeleteConversation = useCallback(async (conversation) => {
    try {
      await deleteConversation(conversation.id);
      const remaining = conversations.filter((item) => item.id !== conversation.id);
      setConversations(remaining);
      if (activeConversation?.id === conversation.id) {
        setActiveConversation(remaining[0] || null);
        setMessages([]);
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete chat.");
    }
  }, [activeConversation, conversations]);

  const handleUploadFiles = useCallback(async (files) => {
    try {
      const uploaded = [];
      for (const file of files) {
        const data = await uploadAttachment({
          file,
          conversationId: activeConversation?.id,
        });
        uploaded.push({
          ...data.attachment,
          localId: `${Date.now()}-${file.name}`,
          name: data.attachment.name,
        });
      }
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (error) {
      toast.error(error.message || "Failed to upload file.");
    }
  }, [activeConversation]);

  const handleSend = useCallback(async (text) => {
    if (streaming) return;
    let conversation = activeConversation;
    try {
      setStreaming(true);
      if (!conversation?.id) {
        const data = await createConversation({ model: selectedModel });
        conversation = data.conversation;
        setActiveConversation(conversation);
        setConversations((prev) => [conversation, ...prev]);
      }

      const optimisticUser = buildOptimisticUserMessage(text, attachments);
      const optimisticAssistant = buildOptimisticAssistantMessage();
      setMessages((prev) => [...prev, optimisticUser, optimisticAssistant]);

      await streamChat({
        conversationId: conversation.id,
        message: text,
        model: selectedModel,
        attachmentIds: attachments.map((item) => item.id),
        onEvent: (event) => {
          if (event.event === "delta") {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === optimisticAssistant.id
                  ? { ...msg, content: `${msg.content || ""}${event.data}` }
                  : msg
              )
            );
          } else if (event.event === "tool") {
            setMessages((prev) => [
              ...prev,
              {
                id: `tool-${Date.now()}-${event.data.name}`,
                role: "tool",
                content: JSON.stringify(event.data.result, null, 2),
                tool_name: event.data.name,
                created_at: new Date().toISOString(),
              },
            ]);
          } else if (event.event === "tool_error") {
            const err = event.data?.error || "A tool error occurred.";
            toast.error(err);
          } else if (event.event === "done") {
            const assistantMessage = event.data?.assistant_message;
            const conv = event.data?.conversation;
            const responsePayload = event.data?.response;
            const toolErrors = event.data?.tool_errors;
            if (Array.isArray(toolErrors) && toolErrors.length > 0) {
              toast.error(toolErrors[0]);
            }
            if (assistantMessage) {
              const merged = {
                ...assistantMessage,
                tool_payload: assistantMessage.tool_payload ?? (responsePayload && typeof responsePayload === "object" && (responsePayload.type || responsePayload.ui_type) ? responsePayload : undefined),
              };
              setMessages((prev) =>
                prev.map((msg) => (msg.id === optimisticAssistant.id ? merged : msg))
              );
            }
            if (conv) setActiveConversation(conv);
            refreshConversations();
            getMyUsage().then(setUsage).catch(() => {});
          } else if (event.event === "error") {
            toast.error(event.data || "Something went wrong.");
            setMessages((prev) => prev.filter((msg) => msg.id !== optimisticAssistant.id));
          }
        },
      });

      setAttachments([]);
    } catch (error) {
      toast.error(error.message || "Failed to send message.");
      setMessages((prev) => prev.filter((msg) => !String(msg.id).startsWith("assistant-")));
    } finally {
      setStreaming(false);
    }
  }, [activeConversation, attachments, refreshConversations, selectedModel, streaming]);

  const reloadActiveConversation = useCallback(async () => {
    if (!activeConversation?.id) return;
    await loadMessages(activeConversation);
    const usageData = await getMyUsage().catch(() => null);
    if (usageData) setUsage(usageData);
    await refreshConversations();
  }, [activeConversation, loadMessages, refreshConversations]);

  const handleRegenerate = useCallback(async (message) => {
    try {
      setStreaming(true);
      await regenerateMessage(message.id, selectedModel);
      await reloadActiveConversation();
    } catch (error) {
      toast.error(error.message || "Failed to regenerate response.");
    } finally {
      setStreaming(false);
    }
  }, [reloadActiveConversation, selectedModel]);

  const handleContinue = useCallback(async (message) => {
    try {
      setStreaming(true);
      await continueMessage(message.id, selectedModel);
      await reloadActiveConversation();
    } catch (error) {
      toast.error(error.message || "Failed to continue response.");
    } finally {
      setStreaming(false);
    }
  }, [reloadActiveConversation, selectedModel]);

  const handleEdit = useCallback(async (message) => {
    const nextText = window.prompt("Edit prompt", message.content || "");
    if (!nextText || nextText === message.content) return;
    try {
      setStreaming(true);
      await editMessage(message.id, nextText, selectedModel);
      await reloadActiveConversation();
    } catch (error) {
      toast.error(error.message || "Failed to edit prompt.");
    } finally {
      setStreaming(false);
    }
  }, [reloadActiveConversation, selectedModel]);

  const handleCopy = useCallback(async (message) => {
    try {
      await navigator.clipboard.writeText(message.content || "");
      toast.success("Message copied.");
    } catch {
      toast.error("Failed to copy message.");
    }
  }, []);

  const handleReaction = useCallback(async (message, reaction) => {
    try {
      const data = await reactToMessage(message.id, reaction);
      setMessages((prev) =>
        prev.map((item) => (item.id === message.id ? data.message : item))
      );
    } catch (error) {
      toast.error(error.message || "Failed to update reaction.");
    }
  }, []);

  const handleFormSubmit = useCallback(
    async (conversationId, submitTool, values) => {
      const parts = Object.entries(values || {})
        .filter(([, v]) => v != null && String(v).trim() !== "")
        .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`);
      const messageText =
        parts.length > 0
          ? `Please run the "${submitTool}" action with the following: ${parts.join(", ")}`
          : `Please run the "${submitTool}" action.`;
      await handleSend(messageText);
    },
    [handleSend]
  );

  const visibleModels = useMemo(() => models || [], [models]);

  const workspaceClassName = fullScreen
    ? `${styles.workspace} ${styles.workspaceFullScreen}`
    : styles.workspace;

  const sidebar = (
    <ChatSidebar
      conversations={conversations}
      activeConversationId={activeConversation?.id}
      onSelectConversation={setActiveConversation}
      onCreateConversation={handleCreateConversation}
      onRenameConversation={handleRenameConversation}
      onDeleteConversation={handleDeleteConversation}
      usage={usage}
    />
  );

  const chatPanel = (
    <div className={styles.chatPanel}>
        <div className={styles.panelHeader}>
          <div>
            <h5 className={styles.panelHeaderTitle}>Abyss</h5>
            <div className={styles.panelHeaderSubtitle}>
              {activeConversation?.title ||
                "Ask about enquiries, create or update records, or chat naturally."}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {inlineMode && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => navigate("/ai-chat")}
              >
                Full chat
              </Button>
            )}
            {onClose && (
              <Button variant="light" size="sm" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </div>

        <div className={styles.panelBody}>
          <ChatWindow
            messages={messages}
            streaming={streaming}
            onRegenerate={handleRegenerate}
            onContinue={handleContinue}
            onEdit={handleEdit}
            onCopy={handleCopy}
            onReact={handleReaction}
            onQuickPrompt={handleSend}
            conversationId={activeConversation?.id}
            onFormSubmit={handleFormSubmit}
            onSuggestionClick={(text) => handleSend(text)}
          />
          <ChatInput
            models={visibleModels}
            selectedModel={selectedModel}
            onModelChange={setSelectedModel}
            onSend={handleSend}
            onFilesSelected={handleUploadFiles}
            attachments={attachments}
            onRemoveAttachment={(attachment) =>
              setAttachments((prev) =>
                prev.filter(
                  (item) =>
                    item.localId !== attachment.localId && item.id !== attachment.id
                )
              )
            }
            disabled={streaming}
          />
        </div>
      </div>
  );

  if (inlineMode) {
    return <div className={styles.workspaceInline}>{chatPanel}</div>;
  }

  if (fullScreen && enterpriseLayout) {
    const lastAssistant = [...(messages || [])].reverse().find((m) => m.role === "assistant");
    const insightText =
      lastAssistant?.content ||
      "Ask Abyss about enquiries, conversions, or follow-ups. Insights will appear here.";
    return (
      <FlashAssistantLayout
        rightPanelOpen={rightPanelOpen}
        onToggleRightPanel={() => setRightPanelOpen((o) => !o)}
        conversations={conversations}
        activeConversationId={activeConversation?.id}
        onSelectConversation={setActiveConversation}
        onCreateConversation={handleCreateConversation}
        usage={usage}
        insightText={insightText}
      >
        <div className={flashStyles.workspaceHeader}>
          <AIPortalLogo />
          <h1>Abyss</h1>
          
          {activeConversation?.title && (
            <span className={flashStyles.chatTitle}>{activeConversation.title}</span>
          )}
        </div>
        <div className={flashStyles.chatContainer}>
          <div className={flashStyles.messagesArea}>
            <ChatWindow
              messages={messages}
              streaming={streaming}
              onRegenerate={handleRegenerate}
              onContinue={handleContinue}
              onEdit={handleEdit}
              onCopy={handleCopy}
              onReact={handleReaction}
              onQuickPrompt={handleSend}
              conversationId={activeConversation?.id}
              onFormSubmit={handleFormSubmit}
            />
          </div>
          <div className={flashStyles.chatInput}>
            <div className={flashStyles.recommendedPrompts}>
              <span className={flashStyles.recommendedPromptsLabel}>Try asking</span>
              <div className={flashStyles.recommendedPromptsChips}>
                {FLASH_SUGGESTION_CHIPS.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    className={flashStyles.recommendedPromptChip}
                    onClick={() => handleSend(prompt)}
                    disabled={streaming}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
            <ChatInput
              models={visibleModels}
              selectedModel={selectedModel}
              onModelChange={setSelectedModel}
              onSend={handleSend}
              onFilesSelected={handleUploadFiles}
              attachments={attachments}
              onRemoveAttachment={(attachment) =>
                setAttachments((prev) =>
                  prev.filter(
                    (item) =>
                      item.localId !== attachment.localId && item.id !== attachment.id
                  )
                )
              }
              disabled={streaming}
              placeholder="Ask Abyss anything about your CRM..."
              suggestionChips={FLASH_SUGGESTION_CHIPS}
              compact={true}
            />
          </div>
        </div>
      </FlashAssistantLayout>
    );
  }

  return (
    <div className={workspaceClassName}>
      {fullScreen ? (
        <>
          {chatPanel}
          {sidebar}
        </>
      ) : (
        <>
          {sidebar}
          {chatPanel}
        </>
      )}
    </div>
  );
}
